import React, { useState, useEffect, useMemo } from 'react';
import { X, MapPin, User, Phone, Mail, CreditCard, DollarSign, QrCode, Search } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import './CheckoutModal.css';

const CheckoutModal = ({ isOpen, onClose, carrinho, estabelecimento, onPedidoCriado }) => {
  const [step, setStep] = useState(1); // 1: Dados, 2: Endereço, 3: Pagamento, 4: Confirmação
  const [loading, setLoading] = useState(false);
  
  // Dados do cliente
  const [dadosCliente, setDadosCliente] = useState({
    nome: '',
    telefone: '',
    whatsapp: '',
    email: ''
  });
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [buscandoCEP, setBuscandoCEP] = useState(false);

  // Endereço de entrega
  const [endereco, setEndereco] = useState({
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: estabelecimento?.endereco?.cidade || 'Cachoeiro de Itapemirim',
    estado: estabelecimento?.endereco?.estado || 'ES',
    cep: '',
    referencia: ''
  });

  // Tipo de entrega e pagamento
  const [tipoEntrega, setTipoEntrega] = useState('delivery');
  const [formaPagamento, setFormaPagamento] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [trocoPara, setTrocoPara] = useState('');
  const [horarioMarmita, setHorarioMarmita] = useState('');
  const [clienteEncontrado, setClienteEncontrado] = useState(null);
  const [opcoesPagamentoOpen, setOpcoesPagamentoOpen] = useState(false);
  const [pedidoConfirmacao, setPedidoConfirmacao] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setLoading(false);
      setFormaPagamento('');
      setObservacoes('');
      setTrocoPara('');
      setHorarioMarmita('');
      setClienteEncontrado(null);
      setPedidoConfirmacao(null);
      setOpcoesPagamentoOpen(false);
    }
  }, [isOpen]);

  const temMarmitexNoCarrinho = useMemo(() => {
    const arr = Array.isArray(carrinho) ? carrinho : [];
    return arr.some((item) => {
      const produto = item?.produto || item;
      const categoria = String(produto?.categoria || '').trim().toLowerCase();
      const nome = String(produto?.nome || '').trim().toLowerCase();
      return categoria === 'marmitex' || nome.includes('marmitex');
    });
  }, [carrinho]);

  const carrinhoAceitaCrediario = useMemo(() => {
    const arr = Array.isArray(carrinho) ? carrinho : [];
    if (arr.length === 0) return false;
    return arr.every((item) => {
      const produto = item?.produto || item;
      return produto?.aceitaCrediario === true;
    });
  }, [carrinho]);

  const crediarioDisponivel = useMemo(() => {
    if (!clienteEncontrado) return false;
    if (clienteEncontrado.crediarioHabilitado !== true) return false;
    if (clienteEncontrado.crediarioBloqueado === true) return false;
    if (!clienteEncontrado.crediarioDataVencimento) return false;
    if (!carrinhoAceitaCrediario) return false;
    return true;
  }, [clienteEncontrado, carrinhoAceitaCrediario]);

  useEffect(() => {
    if (formaPagamento === 'fiado' && !crediarioDisponivel) {
      setFormaPagamento('');
    }
  }, [formaPagamento, crediarioDisponivel]);

  const opcoesHorarioMarmita = useMemo(() => {
    const inicio = String(estabelecimento?.configuracoes?.marmitaHorarioInicio || '10:00');
    const fim = String(estabelecimento?.configuracoes?.marmitaHorarioFim || '14:30');
    const intervalo = Number(estabelecimento?.configuracoes?.marmitaIntervaloMin) || 30;

    const parse = (s) => {
      const [hh, mm] = String(s || '').split(':');
      const h = Number(hh);
      const m = Number(mm);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return (h * 60) + m;
    };

    const iniMin = parse(inicio);
    const fimMin = parse(fim);
    const step = Math.max(5, Math.min(180, Number.isFinite(intervalo) ? intervalo : 30));
    if (iniMin == null || fimMin == null || fimMin < iniMin) return [];

    const out = [];
    for (let t = iniMin; t <= fimMin; t += step) {
      const h = String(Math.floor(t / 60)).padStart(2, '0');
      const m = String(t % 60).padStart(2, '0');
      out.push(`${h}:${m}`);
    }
    return out;
  }, [estabelecimento]);

  const normalizarBairro = (bairro) => {
    if (!bairro || typeof bairro !== 'string') return '';
    return bairro
      .trim()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  const buscarTaxaPorBairro = (bairro, taxasPorBairro = [], taxaPadrao = 0) => {
    if (!bairro || !Array.isArray(taxasPorBairro) || taxasPorBairro.length === 0) {
      return { taxa: taxaPadrao, encontrado: false, bairroEncontrado: null };
    }

    const bairroNormalizado = normalizarBairro(bairro);

    let taxaBairro = taxasPorBairro.find((t) => {
      if (!t?.bairro) return false;
      return normalizarBairro(t.bairro) === bairroNormalizado;
    });

    if (!taxaBairro) {
      taxaBairro = taxasPorBairro.find((t) => {
        if (!t?.bairro) return false;
        const bairroTaxaNormalizado = normalizarBairro(t.bairro);
        return (
          bairroTaxaNormalizado.includes(bairroNormalizado) ||
          bairroNormalizado.includes(bairroTaxaNormalizado)
        );
      });
    }

    if (taxaBairro) {
      return {
        taxa: Number(taxaBairro.taxa) || 0,
        encontrado: true,
        bairroEncontrado: taxaBairro.bairro
      };
    }

    return { taxa: taxaPadrao, encontrado: false, bairroEncontrado: null };
  };

  // Calcular totais (o backend vai recalcular, mas mostramos aqui para o usuário)
  const subtotal = carrinho.reduce((acc, item) => {
    if (!item) return acc;
    
    // O item pode ter preco diretamente ou dentro de produto
    const precoItem = item.preco || item.produto?.preco || 0;
    const listaAcrescimos = item.acrescimos || item.adicionais || [];
    const precoAdicionais = (listaAcrescimos || []).reduce((sum, add) => {
      const qtd = Math.min(20, Math.max(1, Number(add?.quantidade || 1)));
      return sum + ((add?.preco || 0) * qtd);
    }, 0);
    const quantidade = item.quantidade || 1;
    return acc + ((precoItem + precoAdicionais) * quantidade);
  }, 0);

  const taxaEntregaPadrao = Number(estabelecimento?.configuracoes?.taxaEntrega) || 0;
  const taxasPorBairro = estabelecimento?.configuracoes?.taxasPorBairro || [];
  const taxaEntregaAtiva = estabelecimento?.configuracoes?.taxaEntregaAtiva !== false;

  const taxaEntrega = tipoEntrega === 'delivery'
    ? (taxaEntregaAtiva ? (Number(buscarTaxaPorBairro(endereco?.bairro, taxasPorBairro, taxaEntregaPadrao).taxa) || 0) : 0)
    : 0;

  const total = subtotal + taxaEntrega;

  // Buscar cliente por telefone
  const buscarClientePorTelefone = async (telefone) => {
    if (!telefone || telefone.length < 10) return;
    
    setBuscandoCliente(true);
    try {
      const response = await api.get(`/api/clientes/buscar/${telefone}?estabelecimento=${estabelecimento?._id}`);
      if (response.data.success && response.data.data.cliente) {
        const cliente = response.data.data.cliente;
        setClienteEncontrado(cliente);
        setDadosCliente({
          nome: cliente.nome || '',
          telefone: cliente.telefone || telefone,
          whatsapp: cliente.whatsapp || cliente.telefone || '',
          email: cliente.email || ''
        });
        
        // Preencher endereço padrão se existir
        if (cliente.enderecos && cliente.enderecos.length > 0) {
          const enderecoPadrao = cliente.enderecos[cliente.enderecoPadrao || 0];
          if (enderecoPadrao) {
            setEndereco({
              cep: enderecoPadrao.cep || '',
              rua: enderecoPadrao.rua || '',
              numero: enderecoPadrao.numero || '',
              complemento: enderecoPadrao.complemento || '',
              bairro: enderecoPadrao.bairro || '',
              cidade: enderecoPadrao.cidade || endereco.cidade,
              estado: enderecoPadrao.estado || endereco.estado,
              referencia: enderecoPadrao.referencia || ''
            });
            toast.success('Dados do cliente carregados!');
          }
        }
      }
    } catch (error) {
      // Cliente não encontrado - não é erro, apenas não existe ainda
    } finally {
      setBuscandoCliente(false);
    }
  };

  // Buscar CEP
  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;

    setBuscandoCEP(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado. Preencha manualmente.');
        return;
      }

      setEndereco(prev => ({
        ...prev,
        cep: cepLimpo,
        rua: data.logradouro || prev.rua,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        estado: data.uf || prev.estado
      }));
      
      toast.success('Endereço encontrado!');
    } catch (error) {
      toast.error('Erro ao buscar CEP. Preencha manualmente.');
    } finally {
      setBuscandoCEP(false);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      // Validar dados do cliente
      if (!dadosCliente.nome || !dadosCliente.telefone) {
        toast.error('Preencha nome e telefone');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      // Validar endereço se for delivery
      if (tipoEntrega === 'delivery') {
        if (!endereco.rua || !endereco.numero || !endereco.bairro) {
          toast.error('Preencha os dados de endereço');
          return;
        }
      }
      setStep(3);
    } else if (step === 3) {
      handleFinalizarPedido();
    }
  };

  const handleFinalizarPedido = async () => {
    if (!estabelecimento?._id) {
      toast.error('Estabelecimento não encontrado');
      return;
    }

    // Verificar se aceita pedidos
    if (estabelecimento?.configuracoes?.aceitaPedidos === false) {
      toast.error('😔 O estabelecimento não está aceitando pedidos no momento. Por favor, tente novamente mais tarde.', {
        duration: 5000
      });
      return;
    }

    setLoading(true);

    try {
      // Validar carrinho
      if (!carrinho || carrinho.length === 0) {
        toast.error('Carrinho vazio');
        return;
      }

      // Preparar itens do pedido
      const itens = carrinho
        .filter(item => {
          // O item pode ter _id diretamente ou dentro de produto
          return item && (item._id || item.produto?._id);
        })
        .map(item => {
          // O _id pode estar diretamente no item ou em item.produto
          const produtoId = item._id || item.produto?._id;

          // Compatibilidade: o carrinho do site usa "adicionais", mas o backend espera "acrescimos"
          const listaAcrescimos = item.acrescimos || item.adicionais || [];
          const acrescimos = Array.isArray(listaAcrescimos)
            ? listaAcrescimos
                .filter(a => a && (a.nome || a.preco != null))
                .map(a => ({
                  _id: a._id,
                  nome: a.nome,
                  preco: Number(a.preco) || 0,
                  quantidade: Math.min(20, Math.max(1, Number(a.quantidade || 1)))
                }))
            : [];

          // Compatibilidade: backend recalcula usando item.variacao.preco quando presente
          const variacao = item.variacao
            ? {
                nome: item.variacao.nome,
                preco: Number(item.variacao.preco) || 0
              }
            : null;
          
          // Montar observações com adicionais
          let obsItem = item.observacoes || '';
          if (Array.isArray(listaAcrescimos) && listaAcrescimos.length > 0) {
            const adicionaisText = listaAcrescimos
              .filter(add => add && add.nome) // Filtrar adicionais inválidos
              .map(a => `${Math.min(20, Math.max(1, Number(a.quantidade || 1)))}x ${a.nome}`)
              .join(', ');
            if (adicionaisText) {
              obsItem = obsItem ? `${obsItem} | Adicionais: ${adicionaisText}` : `Adicionais: ${adicionaisText}`;
            }
          }
          
          return {
            produto: produtoId,
            quantidade: item.quantidade || 1,
            observacoes: obsItem,
            acrescimos,
            variacao
          };
        });

      if (itens.length === 0) {
        toast.error('Nenhum item válido no carrinho');
        return;
      }

      // Preparar dados do pedido
      const pedidoData = {
        estabelecimento: estabelecimento._id,
        cliente: {
          nome: dadosCliente.nome,
          telefone: dadosCliente.telefone,
          whatsapp: dadosCliente.whatsapp || dadosCliente.telefone,
          email: dadosCliente.email
        },
        itens,
        enderecoEntrega: tipoEntrega === 'delivery' ? endereco : null,
        tipoEntrega,
        formaPagamento,
        observacoes,
        horarioMarmita: (temMarmitexNoCarrinho && horarioMarmita) ? horarioMarmita : undefined,
        origem: 'site'
      };

      if (formaPagamento === 'fiado') {
        const venc = clienteEncontrado?.crediarioDataVencimento;
        if (venc) {
          pedidoData.dataVencimentoFiado = venc;
        }
      }

      if (formaPagamento === 'dinheiro' && trocoPara) {
        const valorRecebido = Number(String(trocoPara).replace(',', '.'));

        if (Number.isNaN(valorRecebido) || valorRecebido <= 0) {
          toast.error('Informe um valor válido para o troco');
          return;
        }

        if (valorRecebido < total) {
          toast.error(`Valor para troco insuficiente. Total: R$ ${total.toFixed(2)}`);
          return;
        }

        const troco = Number((valorRecebido - total).toFixed(2));

        pedidoData.valorRecebido = valorRecebido;
        pedidoData.troco = troco;
        pedidoData.pagamentos = [
          {
            tipo: 'dinheiro',
            valor: Number(total.toFixed(2)),
            valorRecebido,
            troco
          }
        ];
      }

      // Salvar/atualizar cliente antes de criar pedido
      try {
        await api.post('/api/clientes', {
          nome: dadosCliente.nome,
          telefone: dadosCliente.telefone,
          whatsapp: dadosCliente.whatsapp || dadosCliente.telefone,
          email: dadosCliente.email,
          estabelecimento: estabelecimento._id,
          endereco: tipoEntrega === 'delivery' ? endereco : null
        });
      } catch (error) {
        console.error('Erro ao salvar cliente:', error);
        // Continuar mesmo se falhar ao salvar cliente
      }

      const response = await api.post('/api/pedidos', pedidoData);

      if (!response?.data || typeof response.data !== 'object') {
        throw new Error('Resposta inválida do servidor ao criar pedido');
      }

      if (response.data.success) {
        const pedidoCriado = response.data.data.pedido;
        setPedidoConfirmacao(pedidoCriado);
        setStep(4);
        setTrocoPara('');
        setHorarioMarmita('');
        if (onPedidoCriado) {
          onPedidoCriado(pedidoCriado);
        }
        toast.success('Pedido criado com sucesso!');
        
        // Redirecionar para aguardando aprovação após 2 segundos
        setTimeout(() => {
          window.location.href = `/aguardando/${pedidoCriado._id}`;
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Erro ao criar pedido');
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Erro ao criar pedido';
      toast.error(message);
      console.error('Erro ao criar pedido:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formaPagamentoLabel = {
    dinheiro: 'Dinheiro',
    pix: 'PIX',
    cartao_credito: 'Cartão de Crédito',
    cartao_debito: 'Cartão de Débito'
  };

  const steps = [
    { id: 1, label: 'Dados' },
    { id: 2, label: 'Entrega' },
    { id: 3, label: 'Pagamento' }
  ];

  return (
    <div className="checkout-overlay" onClick={onClose}>
      <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
        <button className="checkout-close" onClick={onClose}>
          <X size={24} />
        </button>

        {step < 4 && (
          <div className="checkout-stepper">
            {steps.map((s) => (
              <div
                key={s.id}
                className={`checkout-stepper-item ${step === s.id ? 'active' : ''} ${step > s.id ? 'done' : ''}`}
              >
                <div className="checkout-stepper-circle">{s.id}</div>
                <div className="checkout-stepper-label">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="checkout-content">

        {step === 1 && (
          <div className="checkout-step">
            <h2>Seus Dados</h2>
            <div className="form-group">
              <label>
                <User size={18} />
                Nome Completo *
              </label>
              <input
                type="text"
                value={dadosCliente.nome}
                onChange={(e) => setDadosCliente({ ...dadosCliente, nome: e.target.value })}
                placeholder="Seu nome completo"
                required
              />
            </div>
            <div className="form-group">
              <label>
                <Phone size={18} />
                Telefone *
              </label>
              <div className="input-with-button">
                <input
                  type="tel"
                  value={dadosCliente.telefone}
                  onChange={(e) => {
                    const telefone = e.target.value;
                    setDadosCliente({ ...dadosCliente, telefone });
                    // Buscar cliente automaticamente após digitar telefone completo
                    if (telefone.length >= 10) {
                      buscarClientePorTelefone(telefone);
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value.length >= 10) {
                      buscarClientePorTelefone(e.target.value);
                    }
                  }}
                  placeholder="(28) 99999-9999"
                  required
                />
                {buscandoCliente && (
                  <div className="loading-indicator">
                    <div className="spinner-small"></div>
                  </div>
                )}
              </div>
              <small className="form-hint">Digite o telefone para buscar dados salvos</small>
            </div>
            <div className="form-group">
              <label>
                <Phone size={18} />
                WhatsApp (opcional)
              </label>
              <input
                type="tel"
                value={dadosCliente.whatsapp}
                onChange={(e) => setDadosCliente({ ...dadosCliente, whatsapp: e.target.value })}
                placeholder="(28) 99999-9999"
              />
            </div>
            <div className="form-group">
              <label>
                <Mail size={18} />
                Email (opcional)
              </label>
              <input
                type="email"
                value={dadosCliente.email}
                onChange={(e) => setDadosCliente({ ...dadosCliente, email: e.target.value })}
                placeholder="seu@email.com"
              />
            </div>

            <div className="checkout-actions">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
                type="button"
                className="btn-checkout-back"
              >
                Cancelar
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNext();
                }}
                type="button"
                className="btn-checkout-next"
              >
                Continuar →
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="checkout-step">
            <h2>Entrega</h2>
            
            <div className="tipo-entrega-group">
              <button
                className={`tipo-entrega-btn ${tipoEntrega === 'delivery' ? 'active' : ''}`}
                onClick={() => setTipoEntrega('delivery')}
              >
                <MapPin size={20} />
                Delivery
              </button>
              <button
                className={`tipo-entrega-btn ${tipoEntrega === 'retirada' ? 'active' : ''}`}
                onClick={() => setTipoEntrega('retirada')}
              >
                <User size={20} />
                Retirada no Local
              </button>
            </div>

            {tipoEntrega === 'delivery' && (
              <>
                <div className="form-group">
                  <label>CEP *</label>
                  <div className="input-with-button">
                    <input
                      type="text"
                      value={endereco.cep}
                      onChange={(e) => {
                        let cep = e.target.value.replace(/\D/g, '');
                        if (cep.length > 8) cep = cep.slice(0, 8);
                        if (cep.length > 5) {
                          cep = cep.slice(0, 5) + '-' + cep.slice(5);
                        }
                        setEndereco({ ...endereco, cep });
                      }}
                      onBlur={(e) => {
                        const cepLimpo = e.target.value.replace(/\D/g, '');
                        if (cepLimpo.length === 8) {
                          buscarCEP(cepLimpo);
                        }
                      }}
                      placeholder="29300-000"
                      required
                    />
                    <button
                      type="button"
                      className="btn-buscar-cep"
                      onClick={() => buscarCEP(endereco.cep)}
                      disabled={buscandoCEP || endereco.cep.replace(/\D/g, '').length !== 8}
                    >
                      {buscandoCEP ? (
                        <div className="spinner-small"></div>
                      ) : (
                        <Search size={18} />
                      )}
                    </button>
                  </div>
                  <small className="form-hint">Digite o CEP e clique na lupa ou aguarde para buscar automaticamente</small>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rua *</label>
                    <input
                      type="text"
                      value={endereco.rua}
                      onChange={(e) => setEndereco({ ...endereco, rua: e.target.value })}
                      placeholder="Nome da rua"
                      required
                    />
                  </div>
                  <div className="form-group form-group-small">
                    <label>Número *</label>
                    <input
                      type="text"
                      value={endereco.numero}
                      onChange={(e) => setEndereco({ ...endereco, numero: e.target.value })}
                      placeholder="123"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Complemento</label>
                  <input
                    type="text"
                    value={endereco.complemento}
                    onChange={(e) => setEndereco({ ...endereco, complemento: e.target.value })}
                    placeholder="Apto, casa, etc."
                  />
                </div>
                <div className="form-group">
                  <label>Bairro *</label>
                  <input
                    type="text"
                    value={endereco.bairro}
                    onChange={(e) => setEndereco({ ...endereco, bairro: e.target.value })}
                    placeholder="Nome do bairro"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Cidade *</label>
                    <input
                      type="text"
                      value={endereco.cidade}
                      onChange={(e) => setEndereco({ ...endereco, cidade: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group form-group-small">
                    <label>Estado *</label>
                    <input
                      type="text"
                      value={endereco.estado}
                      onChange={(e) => setEndereco({ ...endereco, estado: e.target.value })}
                      maxLength={2}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Ponto de Referência</label>
                  <input
                    type="text"
                    value={endereco.referencia}
                    onChange={(e) => setEndereco({ ...endereco, referencia: e.target.value })}
                    placeholder="Ex: Próximo ao mercado"
                  />
                </div>
              </>
            )}

            {tipoEntrega === 'retirada' && (
              <div className="retirada-info">
                <p>📍 <strong>Endereço para retirada:</strong></p>
                <p>{estabelecimento?.endereco?.rua}, {estabelecimento?.endereco?.numero}</p>
                <p>{estabelecimento?.endereco?.bairro} - {estabelecimento?.endereco?.cidade}/{estabelecimento?.endereco?.estado}</p>
              </div>
            )}

            <div className="checkout-actions">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setStep(1);
                }}
                type="button"
                className="btn-checkout-back"
              >
                ← Voltar
              </button>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNext();
                }}
                type="button"
                className="btn-checkout-next"
              >
                Continuar →
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="checkout-step">
            <h2>Pagamento</h2>

            <div className="checkout-step3-simple">
              {temMarmitexNoCarrinho && (
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Horário da Marmita</label>
                  {opcoesHorarioMarmita.length > 0 ? (
                    <select value={horarioMarmita} onChange={(e) => setHorarioMarmita(e.target.value)}>
                      <option value="">Selecione</option>
                      {opcoesHorarioMarmita.map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="time" value={horarioMarmita} onChange={(e) => setHorarioMarmita(e.target.value)} />
                  )}
                  <small className="form-hint">Escolha o horário para entrega/retirada da marmita</small>
                </div>
              )}

              <div className="pagamento-options">
                <button
                  className={`pagamento-option ${formaPagamento === 'dinheiro' ? 'active' : ''}`}
                  onClick={() => {
                    setFormaPagamento('dinheiro');
                    setOpcoesPagamentoOpen(true);
                  }}
                  type="button"
                >
                  <DollarSign size={24} />
                  <span>Dinheiro</span>
                </button>
                <button
                  className={`pagamento-option ${formaPagamento === 'pix' ? 'active' : ''}`}
                  onClick={() => {
                    setFormaPagamento('pix');
                    setOpcoesPagamentoOpen(false);
                  }}
                  type="button"
                >
                  <QrCode size={24} />
                  <span>PIX</span>
                </button>
                <button
                  className={`pagamento-option ${formaPagamento === 'cartao_credito' ? 'active' : ''}`}
                  onClick={() => {
                    setFormaPagamento('cartao_credito');
                    setOpcoesPagamentoOpen(false);
                  }}
                  type="button"
                >
                  <CreditCard size={24} />
                  <span>Crédito</span>
                </button>
                <button
                  className={`pagamento-option ${formaPagamento === 'cartao_debito' ? 'active' : ''}`}
                  onClick={() => {
                    setFormaPagamento('cartao_debito');
                    setOpcoesPagamentoOpen(false);
                  }}
                  type="button"
                >
                  <CreditCard size={24} />
                  <span>Débito</span>
                </button>

                {crediarioDisponivel && (
                  <button
                    className={`pagamento-option ${formaPagamento === 'fiado' ? 'active' : ''}`}
                    onClick={() => {
                      setFormaPagamento('fiado');
                      setOpcoesPagamentoOpen(false);
                    }}
                    type="button"
                  >
                    <CreditCard size={24} />
                    <span>Crediário</span>
                  </button>
                )}
              </div>

              {!formaPagamento && (
                <small className="form-hint">Escolha como você vai pagar para continuar</small>
              )}

              <div className="checkout-resumo checkout-resumo--compact">
                <div className="resumo-item">
                  <span>Subtotal:</span>
                  <span>R$ {subtotal.toFixed(2)}</span>
                </div>
                {tipoEntrega === 'delivery' && (
                  <div className="resumo-item">
                    <span>Taxa de Entrega:</span>
                    <span>R$ {taxaEntrega.toFixed(2)}</span>
                  </div>
                )}
                <div className="resumo-total">
                  <span>Total:</span>
                  <strong>R$ {total.toFixed(2)}</strong>
                </div>
              </div>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpcoesPagamentoOpen(prev => !prev);
                }}
                className="btn-checkout-secondary"
                type="button"
              >
                {opcoesPagamentoOpen ? 'Ocultar opções' : 'Opções (opcional)'}
              </button>

              {opcoesPagamentoOpen && (
                <div className="checkout-step3-advanced">
                  {formaPagamento === 'dinheiro' && (
                    <div className="form-group">
                      <label>Troco para quanto? (opcional)</label>
                      <input
                        type="text"
                        value={trocoPara}
                        onChange={(e) => setTrocoPara(e.target.value)}
                        placeholder={`Ex: ${total.toFixed(2)}`}
                      />
                      <small className="form-hint">Informe o valor que você vai pagar em dinheiro para calcular o troco</small>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Observações do Pedido</label>
                    <textarea
                      value={observacoes}
                      onChange={(e) => setObservacoes(e.target.value)}
                      placeholder="Alguma observação especial?"
                      rows={3}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="checkout-actions">
              <button 
                onClick={() => setStep(2)}
                className="btn-checkout-back"
                type="button"
              >
                Voltar
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!formaPagamento) {
                    toast.error('Escolha a forma de pagamento');
                    return;
                  }
                  handleFinalizarPedido();
                }}
                disabled={loading || !formaPagamento}
                className="btn-checkout-finalizar"
                type="button"
              >
                {loading ? 'Processando...' : `Finalizar • R$ ${total.toFixed(2)}`}
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="checkout-step checkout-confirmacao">
            <div className="confirmacao-icon">✅</div>
            <h2>Pedido Realizado com Sucesso!</h2>

            {pedidoConfirmacao && (
              <div className="confirmacao-resumo">
                <div className="confirmacao-resumo-row">
                  <span>Pedido</span>
                  <strong>
                    {pedidoConfirmacao.numero ? `#${pedidoConfirmacao.numero}` : `#${String(pedidoConfirmacao._id || '').slice(-6).toUpperCase()}`}
                  </strong>
                </div>
                <div className="confirmacao-resumo-row">
                  <span>Entrega</span>
                  <strong>
                    {(pedidoConfirmacao.tipoEntrega || tipoEntrega) === 'retirada' ? 'Retirada no local' : 'Delivery'}
                  </strong>
                </div>
                <div className="confirmacao-resumo-row">
                  <span>Pagamento</span>
                  <strong>
                    {formaPagamentoLabel[pedidoConfirmacao.formaPagamento || formaPagamento] || (pedidoConfirmacao.formaPagamento || formaPagamento || '—')}
                  </strong>
                </div>
                <div className="confirmacao-resumo-total">
                  <span>Total</span>
                  <strong>
                    R$ {Number((pedidoConfirmacao.total ?? total) || 0).toFixed(2)}
                  </strong>
                </div>
              </div>
            )}

            <p className="confirmacao-mensagem">
              Seu pedido foi recebido e está aguardando aprovação.
              Redirecionando para acompanhamento...
            </p>
            <div className="loading-redirect">
              <div className="spinner-small"></div>
              <p>Aguarde...</p>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;

